import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import compressImage from "../../utils/compressAsset";
import {
  MdClose,
  MdCloudUpload,
  MdDelete,
  MdFilterList,
  MdImage,
  MdOutlineVideoLibrary,
  MdSearch,
  MdAddPhotoAlternate,
} from "react-icons/md";
import { FaChevronLeft, FaChevronRight, FaImages } from "react-icons/fa";
import { toast } from "react-toastify";
import useAuth from "../../useAuth";
import { server } from "../../server";

// ─── helpers ────────────────────────────────────────────────────────────────
const MEDIA_TYPES = ["all", "image", "video"];

function MediaBadge({ type }) {
  const isVideo = type === "video";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
        isVideo ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"
      }`}
    >
      {isVideo ? <MdOutlineVideoLibrary /> : <MdImage />}
      {type}
    </span>
  );
}

// ─── Upload Modal ────────────────────────────────────────────────────────────
function UploadModal({ isOpen, onClose, onSuccess, token }) {
  const [mediaType, setMediaType] = useState("image");
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const reset = () => {
    setMediaType("image");
    setPhoto(null);
    setPreview(null);
    setVideoUrl("");
    setCaption("");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhoto(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mediaType === "image" && !photo) {
      toast.error("Please select a photo to upload.");
      return;
    }
    if (mediaType === "video" && !videoUrl.trim()) {
      toast.error("Please enter a video URL.");
      return;
    }

    setUploading(true);
    try {
      const form = new FormData();
      form.append("mediaType", mediaType);
      form.append("caption", caption);

      if (mediaType === "image") {
        // Compress before upload (≤ 0.2 MB, max 1920px)
        const compressed = await compressImage(photo);
        form.append("photo", compressed, photo?.name);
      }

      if (mediaType === "video") form.append("videoUrl", videoUrl);

      await axios.post(`${server}/gallery/new`, form, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success("Media uploaded successfully!");
      handleClose();
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{
          backgroundColor: "rgba(15,23,42,0.6)",
          backdropFilter: "blur(4px)",
        }}
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.92, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.92, opacity: 0, y: 20 }}
          transition={{ type: "spring", stiffness: 300, damping: 25 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center text-white">
                <MdAddPhotoAlternate />
              </div>
              <h2 className="text-lg font-bold text-slate-800">Upload Media</h2>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
            >
              <MdClose />
            </button>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Media Type Toggle */}
            <div>
              <label className="text-xs font-bold uppercase text-slate-500 tracking-wider block mb-2">
                Media Type
              </label>
              <div className="grid grid-cols-2 gap-3">
                {["image", "video"].map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setMediaType(type)}
                    className={`flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all ${
                      mediaType === type
                        ? type === "image"
                          ? "border-blue-600 bg-blue-50 text-blue-700"
                          : "border-purple-600 bg-purple-50 text-purple-700"
                        : "border-slate-200 text-slate-500 hover:border-slate-300"
                    }`}
                  >
                    {type === "image" ? (
                      <MdImage className="text-lg" />
                    ) : (
                      <MdOutlineVideoLibrary className="text-lg" />
                    )}
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Image Upload Area */}
            {mediaType === "image" && (
              <div>
                <label className="text-xs font-bold uppercase text-slate-500 tracking-wider block mb-2">
                  Photo
                </label>
                <div
                  onClick={() => fileRef.current.click()}
                  className="relative cursor-pointer rounded-xl border-2 border-dashed border-slate-300 hover:border-blue-400 transition-colors bg-slate-50 hover:bg-blue-50 overflow-hidden"
                  style={{ minHeight: "160px" }}
                >
                  {preview ? (
                    <img
                      src={preview}
                      alt="preview"
                      className="w-full h-48 object-contain"
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center h-40 gap-2 text-slate-400">
                      <MdCloudUpload className="text-4xl" />
                      <span className="text-sm font-medium">
                        Click to select image
                      </span>
                      <span className="text-xs">
                        JPG, PNG, WEBP, GIF up to 10MB
                      </span>
                    </div>
                  )}
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFile}
                    className="hidden"
                  />
                </div>
              </div>
            )}

            {/* Video URL Input */}
            {mediaType === "video" && (
              <div>
                <label className="text-xs font-bold uppercase text-slate-500 tracking-wider block mb-2">
                  Video URL
                </label>
                <input
                  type="url"
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  placeholder="https://youtube.com/watch?v=..."
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all"
                />
              </div>
            )}

            {/* Caption */}
            <div>
              <label className="text-xs font-bold uppercase text-slate-500 tracking-wider block mb-2">
                Caption{" "}
                <span className="text-slate-400 normal-case font-normal">
                  (optional)
                </span>
              </label>
              <input
                type="text"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Enter a caption..."
                className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
              />
            </div>

            {/* Submit */}
            <motion.button
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={uploading}
              className={`w-full py-3 rounded-xl text-white font-bold text-sm flex items-center justify-center gap-2 transition-all ${
                mediaType === "image"
                  ? "bg-blue-600 hover:bg-blue-700 shadow-sm shadow-blue-200"
                  : "bg-purple-600 hover:bg-purple-700 shadow-lg shadow-purple-200"
              } disabled:opacity-60 disabled:cursor-not-allowed`}
            >
              {uploading ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8z"
                    />
                  </svg>
                  Uploading...
                </>
              ) : (
                <>
                  <MdCloudUpload className="text-lg" />
                  Upload {mediaType === "image" ? "Photo" : "Video"}
                </>
              )}
            </motion.button>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ─── Media Card ──────────────────────────────────────────────────────────────
function MediaCard({ item, onDelete }) {
  const isVideo = item.mediaType === "video";

  // Extract YouTube embed URL
  const getYouTubeEmbed = (url) => {
    if (!url) return null;
    const match = url.match(
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?/]+)/,
    );
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  };

  const embedUrl = isVideo ? getYouTubeEmbed(item.mediaUrl) : null;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.3 }}
      className="group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 border border-slate-100"
    >
      {/* Media */}
      <div className="relative aspect-video bg-slate-100">
        {isVideo ? (
          embedUrl ? (
            <iframe
              src={embedUrl}
              title={item.caption || "Video"}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-slate-400 bg-slate-900">
              <MdOutlineVideoLibrary className="text-4xl text-purple-400" />
              <a
                href={item.mediaUrl}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-purple-300 underline break-all px-4 text-center"
              >
                {item.mediaUrl}
              </a>
            </div>
          )
        ) : (
          <img
            src={item.mediaUrl}
            alt={item.caption || "Gallery image"}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        )}

        {/* Hover overlay for images */}
        {!isVideo && (
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        )}

        {/* Delete button */}
        <button
          onClick={() => onDelete(item.id)}
          className="absolute top-2 right-2 w-8 h-8 rounded-full bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 shadow-lg hover:bg-red-700 hover:scale-110 z-10"
          title="Delete"
        >
          <MdDelete className="text-sm" />
        </button>
      </div>

      {/* Footer */}
      <div className="px-4 py-3">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-medium text-slate-700 truncate flex-1">
            {item.caption || (
              <span className="text-slate-400 italic">No caption</span>
            )}
          </p>
          <MediaBadge type={item.mediaType} />
        </div>
        <p className="text-xs text-slate-400 mt-1">
          {new Date(item.createdAt).toLocaleDateString("en-IN", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })}
        </p>
        {item.createdByUser && (
          <p className="text-xs text-slate-400 truncate">
            by{" "}
            <span className="font-medium text-slate-500">
              {item.createdByUser.fullName}
            </span>
          </p>
        )}
      </div>
    </motion.div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function GalleryPage() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Filters & pagination
  const [mediaTypeFilter, setMediaTypeFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [pageSize] = useState(12);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const fetchItems = async (pg = pageNo) => {
    setLoading(true);
    try {
      const params = {
        pageNo: pg,
        pageSize,
        ...(mediaTypeFilter !== "all" && { mediaType: mediaTypeFilter }),
        ...(search && { searchParams: search }),
      };
      const res = await axios.get(`${server}/gallery/all`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(res.data.data || []);
      setTotalPages(res.data.totalPages || 1);
      setTotalCount(res.data.count || 0);
    } catch {
      toast.error("Failed to load gallery.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchItems(pageNo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, mediaTypeFilter, search, pageNo]);

  // Reset to page 1 when filter/search changes
  useEffect(() => {
    setPageNo(1);
  }, [mediaTypeFilter, search]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this media item?")) return;
    try {
      await axios.delete(`${server}/gallery/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Deleted successfully.");
      fetchItems(pageNo);
    } catch {
      toast.error("Failed to delete.");
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearch("");
  };

  return (
    <div className="p-4 max-w-[1400px] mx-auto min-h-screen bg-gray-50 dark:bg-slate-950 text-gray-800 dark:text-slate-100 font-sans transition-colors duration-300">
      {/* Compact Top Header Bar */}
      <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl px-5 py-3 mb-4 shadow-xs flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 rounded-xl shadow-xs">
            <FaImages size={18} />
          </div>
          <div>
            <h1 className="text-lg font-black text-gray-900 dark:text-white leading-tight">Media & Event Gallery</h1>
            <p className="text-xs text-gray-500 dark:text-slate-400 font-semibold">Manage school event photos, video streams, and media publications</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-3.5 py-1.5 rounded-xl font-black text-xs transition shadow-xs flex items-center gap-1.5"
          >
            <MdAddPhotoAlternate size={14} /> Upload Media
          </button>
        </div>
      </div>

      {/* ── Filters Bar ───────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        {/* Media Type Filter */}
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl p-1 shadow-sm">
          <MdFilterList className="text-slate-400 ml-2" />
          {MEDIA_TYPES.map((t) => (
            <button
              key={t}
              onClick={() => setMediaTypeFilter(t)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-all ${
                mediaTypeFilter === t
                  ? "bg-blue-600 text-white shadow"
                  : "text-slate-500 hover:bg-slate-100"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Search */}
        <form
          onSubmit={handleSearchSubmit}
          className="flex-1 min-w-[200px] max-w-sm"
        >
          <div className="relative flex items-center">
            <MdSearch className="absolute left-3 text-slate-400 text-lg" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by caption, category..."
              className="w-full pl-9 pr-8 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
            />
            {searchInput && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="absolute right-2 text-slate-400 hover:text-slate-700"
              >
                <MdClose />
              </button>
            )}
          </div>
        </form>
      </div>

      {/* ── Grid ──────────────────────────────────────────────────── */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="rounded-2xl bg-white border border-slate-100 overflow-hidden animate-pulse"
            >
              <div className="aspect-video bg-slate-200" />
              <div className="p-4 space-y-2">
                <div className="h-3 bg-slate-200 rounded w-3/4" />
                <div className="h-3 bg-slate-100 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-slate-400">
          <FaImages className="text-6xl mb-4 text-slate-300" />
          <p className="text-lg font-semibold text-slate-500">No media found</p>
          <p className="text-sm mt-1">
            Upload a photo or video to get started.
          </p>
          <button
            onClick={() => setIsModalOpen(true)}
            className="mt-5 flex items-center gap-2 bg-blue-600 text-white text-sm font-bold px-5 py-2.5 rounded-xl hover:bg-blue-700 transition-colors"
          >
            <MdAddPhotoAlternate /> Upload Media
          </button>
        </div>
      ) : (
        <motion.div
          layout
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4"
        >
          <AnimatePresence>
            {items.map((item) => (
              <MediaCard key={item.id} item={item} onDelete={handleDelete} />
            ))}
          </AnimatePresence>
        </motion.div>
      )}

      {/* ── Pagination ────────────────────────────────────────────── */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-8">
          <button
            onClick={() => setPageNo((p) => Math.max(1, p - 1))}
            disabled={pageNo === 1}
            className="w-9 h-9 flex items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-blue-50 hover:border-blue-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
          >
            <FaChevronLeft className="text-xs" />
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(
              (p) => p === 1 || p === totalPages || Math.abs(p - pageNo) <= 2,
            )
            .reduce((acc, p, idx, arr) => {
              if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
              acc.push(p);
              return acc;
            }, [])
            .map((p, i) =>
              p === "..." ? (
                <span key={i} className="text-slate-400 text-sm px-1">
                  …
                </span>
              ) : (
                <button
                  key={p}
                  onClick={() => setPageNo(p)}
                  className={`w-9 h-9 rounded-xl text-sm font-bold transition-all shadow-sm ${
                    pageNo === p
                      ? "bg-blue-600 text-white shadow-blue-200"
                      : "bg-white border border-slate-200 text-slate-600 hover:bg-blue-50 hover:border-blue-300"
                  }`}
                >
                  {p}
                </button>
              ),
            )}

          <button
            onClick={() => setPageNo((p) => Math.min(totalPages, p + 1))}
            disabled={pageNo === totalPages}
            className="w-9 h-9 flex items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-blue-50 hover:border-blue-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
          >
            <FaChevronRight className="text-xs" />
          </button>
        </div>
      )}

      {/* ── Upload Modal ──────────────────────────────────────────── */}
      <UploadModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={() => fetchItems(1)}
        token={token}
      />
    </div>
  );
}
